import { useMemo, useState } from 'react'

import {
  FIRST_PARTY_CHILD_LEVEL,
  FIRST_PARTY_LEVEL_LABELS,
  type FirstPartyLevel,
  type FirstPartyMetricKey,
  type FirstPartyTrafficMetrics,
  type FirstPartyTrafficQuery,
  type FirstPartyTrafficReport,
  type FirstPartyTrafficRow,
} from '@/api/firstPartyTraffic'
import { deltaPresentation, isNotTracked, NOT_TRACKED_EXPLANATION, NOT_TRACKED_LABEL, notTrackedLast, type DeltaPresentation } from '@/components/dataQuality'
import { DeltaBadge } from '@/components/DeltaBadge'
import { TooltipBadge } from '@/components/TooltipBadge'
import { changeTone, formatChange, Icon, percentChange } from '@/design'

import { childQuery, useFirstPartyReport } from './firstPartyQuery'
import { FIRST_PARTY_COLUMNS, formatFirstPartyMetric } from './firstPartyMetrics'
import { firstPartyErrorMessage } from './errors'

const COLUMN_COUNT = FIRST_PARTY_COLUMNS.length + 1
const INDENT_PX = 20
const FALLBACK = 'This level could not be loaded just now. Try again in a moment.'

/** A figure and, when compared, its change against the previous period. */
function MetricValue({
  metric,
  value,
  previous,
  currency,
  comparison,
}: {
  metric: FirstPartyMetricKey
  value: number | null
  previous: FirstPartyTrafficMetrics | null
  currency: string
  comparison: DeltaPresentation
}) {
  const before = previous ? previous[metric] : null
  const change = previous && value !== null && before !== null ? percentChange(value, before) : null
  return (
    <span className="tr-metric">
      <span>{formatFirstPartyMetric(metric, value, currency)}</span>
      {previous ? (
        <DeltaBadge
          className="tr-delta"
          text={change === null ? '—' : formatChange(change)}
          tone={changeTone(change, 'up_good')}
          presentation={comparison}
        />
      ) : null}
    </span>
  )
}


function StateRow({ depth, children, alert = false }: { depth: number; children: React.ReactNode; alert?: boolean }) {
  return (
    <tr>
      <td colSpan={COLUMN_COUNT} role={alert ? 'alert' : undefined} className="tr-child-state" style={{ paddingLeft: `${depth * INDENT_PX}px` }}>
        {children}
      </td>
    </tr>
  )
}

function RowGroup({
  row,
  level,
  base,
  currency,
  depth,
  comparison,
}: {
  row: FirstPartyTrafficRow
  level: FirstPartyLevel
  base: FirstPartyTrafficQuery
  currency: string
  depth: number
  comparison: DeltaPresentation
}) {
  const [expanded, setExpanded] = useState(false)
  // The unattributed row has no channel, so nothing below it.
  const childLevel = row.key === null ? null : FIRST_PARTY_CHILD_LEVEL[level]
  const notTracked = isNotTracked(row)
  const name = (
    <>
      {depth > 0 ? <span className="tr-level">{FIRST_PARTY_LEVEL_LABELS[level]}</span> : null}
      {notTracked ? (
        <TooltipBadge tone="neutral" label={NOT_TRACKED_LABEL} tooltip={NOT_TRACKED_EXPLANATION} className="tr-not-tracked" />
      ) : (
        <span>{row.label}</span>
      )}
    </>
  )
  return (
    <>
      <tr data-level={level} data-not-tracked={notTracked || undefined}>
        <th scope="row" style={{ paddingLeft: `${depth * INDENT_PX}px` }}>
          {childLevel ? (
            <button type="button" className="tr-expand" aria-expanded={expanded} onClick={() => setExpanded((open) => !open)}>
              <Icon name="chevronDown" className={expanded ? 'tr-expand__icon' : 'tr-expand__icon tr-expand__icon--closed'} />
              {name}
            </button>
          ) : (
            <span className="tr-leaf">{name}</span>
          )}
        </th>
        {FIRST_PARTY_COLUMNS.map((column) => (
          <td key={column.key} data-align="end">
            <MetricValue metric={column.key} value={row[column.key]} previous={row.previous} currency={currency} comparison={comparison} />
          </td>
        ))}
      </tr>
      {expanded && childLevel && row.key !== null ? (
        <LevelRows
          level={childLevel}
          path={[...row.path, row.key]}
          base={base}
          currency={currency}
          depth={depth + 1}
          comparison={comparison}
        />
      ) : null}
    </>
  )
}

/**
 * One expanded row's children, fetched on first expand and cached under their own
 * query key (level and path), so collapsing and re-expanding costs nothing.
 */
function LevelRows({
  level,
  path,
  base,
  currency,
  depth,
  comparison,
}: {
  level: FirstPartyLevel
  path: readonly string[]
  base: FirstPartyTrafficQuery
  currency: string
  depth: number
  comparison: DeltaPresentation
}) {
  const query = useMemo(() => childQuery(base, level, path), [base, level, path])
  const result = useFirstPartyReport(query)
  const noun = FIRST_PARTY_LEVEL_LABELS[level].toLowerCase()

  if (result.isPending) return <StateRow depth={depth}>Loading {noun}s…</StateRow>
  if (!result.data)
    return (
      <StateRow depth={depth} alert>
        {firstPartyErrorMessage(result.error, FALLBACK)}
      </StateRow>
    )
  if (result.data.rows.length === 0) return <StateRow depth={depth}>No {noun}s in this period.</StateRow>
  return (
    <>
      {result.data.rows.map((row) => (
        <RowGroup key={row.key ?? 'unattributed'} row={row} level={level} base={base} currency={currency} depth={depth} comparison={comparison} />
      ))}
      {result.nextPageError ? (
        <StateRow depth={depth} alert>
          {firstPartyErrorMessage(result.nextPageError, FALLBACK)}
        </StateRow>
      ) : null}
      {result.hasNextPage ? (
        <StateRow depth={depth}>
          <button type="button" className="ci-button" data-variant="ghost" disabled={result.isFetchingNextPage} onClick={result.fetchNextPage}>
            {result.isFetchingNextPage ? 'Loading more…' : `Load more ${noun}s`}
          </button>
        </StateRow>
      ) : null}
    </>
  )
}

/** Channels, each expanding level by level down to its landing pages. */
export function FirstPartyTable({
  report,
  base,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  nextPageError,
}: {
  report: FirstPartyTrafficReport
  base: FirstPartyTrafficQuery
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => void
  nextPageError: unknown
}) {
  const caption = 'First-party traffic by channel, expandable to landing page'
  // The same range and comparison at every level, so one presentation for every change in the tree.
  const comparison = deltaPresentation(report.dataQuality)
  return (
    <div className="ci-table-wrap" role="region" aria-label={caption} tabIndex={0}>
      <table className="ci-table tr-tree">
        <caption className="ci-visually-hidden">{caption}</caption>
        <thead>
          <tr>
            <th scope="col">Channel › source › medium › campaign › creative › landing page</th>
            {FIRST_PARTY_COLUMNS.map((column) => (
              <th key={column.key} scope="col" data-align="end">
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Not tracked last, wherever the API sent it. */}
          {notTrackedLast(report.rows).map((row) => (
            <RowGroup
              key={row.key ?? 'unattributed'}
              row={row}
              level={report.level}
              base={base}
              currency={report.currency}
              depth={0}
              comparison={comparison}
            />
          ))}
          {nextPageError ? (
            <StateRow depth={0} alert>
              {firstPartyErrorMessage(nextPageError, FALLBACK)}
            </StateRow>
          ) : null}
          {hasNextPage ? (
            <StateRow depth={0}>
              <button type="button" className="ci-button" data-variant="ghost" disabled={isFetchingNextPage} onClick={fetchNextPage}>
                {isFetchingNextPage ? 'Loading more…' : 'Load more channels'}
              </button>
            </StateRow>
          ) : null}
        </tbody>
      </table>
    </div>
  )
}

/** The whole report's figures (the parent's own: sessions and users distinct), with the same deltas as the rows. */
export function FirstPartyTotals({ report }: { report: FirstPartyTrafficReport }) {
  return (
    <dl className="tr-totals" aria-label="Totals">
      {FIRST_PARTY_COLUMNS.map((column) => (
        <div key={column.key}>
          <dt>{column.header}</dt>
          <dd>
            <MetricValue
              metric={column.key}
              value={report.totals[column.key]}
              previous={report.totalsPrevious}
              currency={report.currency}
              comparison={deltaPresentation(report.dataQuality)}
            />
          </dd>
        </div>
      ))}
    </dl>
  )
}
